/**
 * Compose API client
 *
 * All API calls for composable creator setup and user compose sessions.
 */

import { apiClient, type ApiResponse } from './client';
import type { SigningPayload, XamanPayloadResponse } from '../types/signing';
import { toSigningPayload } from '../lib/wallet';

export type { XamanPayloadResponse };

/** Phase 4 signing intent — always present; carries client-signing txJson for joey-wc. */
interface SigningIntentRef {
  intentId: string;
  provider: string;
  txJson?: Record<string, unknown>;
}

// ==================== Types ====================

export interface TraitValue {
  id: string;
  name: string;
  image_key: string;
  image_url: string | null;
  total_supply: number;
  minted_count: number;
  remaining_count: number;
}

export interface TraitCategory {
  id: string;
  launch_id: string;
  name: string;
  z_index: number;
  values: TraitValue[];
}

export interface ComposeSession {
  id: string;
  launch_id: string;
  user_address: string;
  status: string;
  input_nft_token_ids: string[];
  expected_traits: Array<{ category: string; value: string }>;
  composition_fee_drops: number;
  composition_fee_per_nft_drops: number;
  fee_payment_tx_hash: string | null;
  fee_paid_at: string | null;
  burns_completed: Array<{
    nft_token_id: string;
    burn_tx_hash: string;
    burned_at: string;
    traits: Array<{ trait_type: string; value: string }>;
  }>;
  burns_remaining: string[];
  result_nft_token_id: string | null;
  result_image_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface ComposeNFT {
  nft_token_id: string;
  name: string | null;
  image_url: string | null;
  type: string;
  z_index: number;
  traits: Array<{ trait_type: string; value: string }>;
  trait_count: number;
  trait_layers: Array<{ url: string; z_index: number; category: string }>;
  rarity_score: number | null;
}

export interface ComposeValidation {
  valid: boolean;
  errors: string[];
  combined_traits: Array<{ category: string; value: string }>;
  overwritten_traits: Array<{ category: string; original_value: string; new_value: string }>;
  trait_count: number;
  composition_fee_drops: number;
  composition_fee_xrp: number;
  nft_count: number;
}

export interface ComposeStats {
  total_composed: number;
  total_burned: number;
  total_composition_fee_drops: number;
  creator_revenue_drops: number;
  platform_revenue_drops: number;
  unique_composers: number;
  avg_traits_per_compose: number;
  total_supply: number;
  minted_count: number;
  compose_enabled: boolean;
  composition_fee_per_nft_drops: number;
  composition_fee_per_nft_xrp: number;
}

export interface ComposeHistoryEntry {
  id: string;
  user_address: string;
  burned_nft_token_ids: string[];
  burned_trait_count: number;
  result_nft_token_id: string;
  result_traits: Array<{ category: string; value: string }>;
  result_trait_count: number;
  result_image_url: string | null;
  composition_fee_drops: number;
  composed_at: string;
}

export interface CompositionPreview {
  baseImageUrl: string;
  baseImageZIndex: number;
  layers: Array<{ cdnUrl: string; zIndex: number }>;
}

// ==================== Creator API ====================

export async function getTraitCategories(
  launchId: string
): Promise<{ categories: TraitCategory[]; base_image_z_index: number }> {
  const res = await apiClient.get<ApiResponse<{ categories: TraitCategory[]; base_image_z_index: number }>>(
    `/launches/${launchId}/trait-categories`
  );
  return {
    categories: res.data?.categories || [],
    base_image_z_index: res.data?.base_image_z_index ?? 0,
  };
}

export async function createTraitCategory(
  launchId: string,
  data: { name: string; z_index: number }
): Promise<TraitCategory> {
  const res = await apiClient.post<ApiResponse<{ category: TraitCategory }>>(
    `/launches/${launchId}/trait-categories`,
    data
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to create category');
  return res.data.category;
}

export async function updateTraitCategory(
  launchId: string,
  categoryId: string,
  data: { name?: string; z_index?: number }
): Promise<TraitCategory> {
  const res = await apiClient.put<ApiResponse<{ category: TraitCategory }>>(
    `/launches/${launchId}/trait-categories/${categoryId}`,
    data
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to update category');
  return res.data.category;
}

export async function deleteTraitCategory(
  launchId: string,
  categoryId: string
): Promise<void> {
  const res = await apiClient.delete<ApiResponse<unknown>>(
    `/launches/${launchId}/trait-categories/${categoryId}`
  );
  if (!res.success) throw new Error(res.error || 'Failed to delete category');
}

export async function createTraitValues(
  launchId: string,
  categoryId: string,
  values: Array<{ name: string; total_supply: number; content_type: string }>
): Promise<Array<{ name: string; valueId: string; uploadUrl: string; cdnUrl: string; key: string }>> {
  const res = await apiClient.post<ApiResponse<{
    values: Array<{ name: string; valueId: string; uploadUrl: string; cdnUrl: string; key: string }>;
  }>>(`/launches/${launchId}/trait-categories/${categoryId}/values`, { values });
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to create values');
  return res.data.values;
}

export async function updateTraitValue(
  launchId: string,
  categoryId: string,
  valueId: string,
  data: { name?: string; total_supply?: number }
): Promise<void> {
  const res = await apiClient.put<ApiResponse<unknown>>(
    `/launches/${launchId}/trait-categories/${categoryId}/values/${valueId}`,
    data
  );
  if (!res.success) throw new Error(res.error || 'Failed to update value');
}

export async function deleteTraitValue(
  launchId: string,
  categoryId: string,
  valueId: string
): Promise<void> {
  const res = await apiClient.delete<ApiResponse<unknown>>(
    `/launches/${launchId}/trait-categories/${categoryId}/values/${valueId}`
  );
  if (!res.success) throw new Error(res.error || 'Failed to delete value');
}

export async function updateBaseImageZIndex(
  launchId: string,
  baseImageZIndex: number
): Promise<void> {
  const res = await apiClient.put<ApiResponse<unknown>>(
    `/launches/${launchId}/base-image-z-index`,
    { base_image_z_index: baseImageZIndex }
  );
  if (!res.success) throw new Error(res.error || 'Failed to update base image position');
}

export async function presignBaseImage(
  launchId: string,
  contentType: string = 'image/png'
): Promise<{ uploadUrl: string; cdnUrl: string; key: string }> {
  const res = await apiClient.post<ApiResponse<{ uploadUrl: string; cdnUrl: string; key: string }>>(
    `/launches/${launchId}/base-image/presign`,
    { content_type: contentType }
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to generate presigned URL');
  return res.data;
}

export async function getCompositionPreview(
  launchId: string,
  traitValueIds: string[]
): Promise<CompositionPreview> {
  const res = await apiClient.post<ApiResponse<CompositionPreview>>(
    `/launches/${launchId}/preview-composition`,
    { trait_value_ids: traitValueIds }
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to get preview');
  return res.data;
}

// ==================== User Compose API ====================

export async function getMyComposeNFTs(launchId: string): Promise<ComposeNFT[]> {
  const res = await apiClient.get<ApiResponse<{ nfts: ComposeNFT[] }>>(
    `/launches/${launchId}/compose/my-nfts`
  );
  return res.data?.nfts || [];
}

export async function validateCompose(
  launchId: string,
  nftTokenIds: string[]
): Promise<ComposeValidation> {
  const res = await apiClient.post<ApiResponse<ComposeValidation>>(
    `/launches/${launchId}/compose/validate`,
    { nft_token_ids: nftTokenIds }
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Validation failed');
  return res.data;
}

export async function createComposeSession(
  launchId: string,
  nftTokenIds: string[]
): Promise<ComposeSession> {
  const res = await apiClient.post<ApiResponse<{ session: ComposeSession }>>(
    `/launches/${launchId}/compose/sessions`,
    { nft_token_ids: nftTokenIds }
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to create session');
  return res.data.session;
}

export async function getComposeSession(
  launchId: string,
  sessionId: string
): Promise<ComposeSession> {
  const res = await apiClient.get<ApiResponse<{ session: ComposeSession }>>(
    `/launches/${launchId}/compose/sessions/${sessionId}`
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Session not found');
  return res.data.session;
}

export async function reportFeePaid(
  launchId: string,
  sessionId: string,
  txHash: string
): Promise<ComposeSession> {
  const res = await apiClient.post<ApiResponse<{ session: ComposeSession }>>(
    `/launches/${launchId}/compose/sessions/${sessionId}/fee-paid`,
    { tx_hash: txHash }
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to report fee payment');
  return res.data.session;
}

export async function reportBurn(
  launchId: string,
  sessionId: string,
  nftTokenId: string,
  txHash: string
): Promise<void> {
  const res = await apiClient.post<ApiResponse<unknown>>(
    `/launches/${launchId}/compose/sessions/${sessionId}/burn`,
    { nft_token_id: nftTokenId, tx_hash: txHash }
  );
  if (!res.success) throw new Error(res.error || 'Failed to report burn');
}

export async function finishComposeEarly(
  launchId: string,
  sessionId: string
): Promise<ComposeSession> {
  const res = await apiClient.post<ApiResponse<{ session: ComposeSession }>>(
    `/launches/${launchId}/compose/sessions/${sessionId}/finish`
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to finish early');
  return res.data.session;
}

export async function createFeePayload(
  launchId: string,
  sessionId: string
): Promise<SigningPayload> {
  const res = await apiClient.post<ApiResponse<{ signingIntent?: SigningIntentRef; payload?: XamanPayloadResponse }>>(
    `/launches/${launchId}/compose/sessions/${sessionId}/fee-payload`
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to create fee payload');
  return toSigningPayload(res.data.signingIntent ?? res.data.payload);
}

export async function createBurnPayload(
  launchId: string,
  sessionId: string,
  nftTokenId: string
): Promise<SigningPayload> {
  const res = await apiClient.post<ApiResponse<{ signingIntent?: SigningIntentRef; payload?: XamanPayloadResponse }>>(
    `/launches/${launchId}/compose/sessions/${sessionId}/burn-payload`,
    { nft_token_id: nftTokenId }
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to create burn payload');
  return toSigningPayload(res.data.signingIntent ?? res.data.payload);
}

export async function cancelComposeSession(
  launchId: string,
  sessionId: string
): Promise<ComposeSession> {
  const res = await apiClient.post<ApiResponse<{ session: ComposeSession }>>(
    `/launches/${launchId}/compose/sessions/${sessionId}/cancel`
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to cancel session');
  return res.data.session;
}

export async function getIncompleteComposeSessions(
  launchId: string
): Promise<ComposeSession[]> {
  const res = await apiClient.get<ApiResponse<{ sessions: ComposeSession[] }>>(
    `/launches/${launchId}/compose/sessions/incomplete`
  );
  return res.data?.sessions || [];
}

export async function getComposeStats(launchId: string): Promise<ComposeStats> {
  const res = await apiClient.get<ApiResponse<{ stats: ComposeStats }>>(
    `/launches/${launchId}/compose/stats`
  );
  if (!res.success || !res.data) throw new Error(res.error || 'Failed to get stats');
  return res.data.stats;
}

export async function getComposeHistory(
  launchId: string,
  page = 1,
  limit = 20
): Promise<{ entries: ComposeHistoryEntry[]; total: number }> {
  const res = await apiClient.get<ApiResponse<{ entries: ComposeHistoryEntry[]; total: number }>>(
    `/launches/${launchId}/compose/history?page=${page}&limit=${limit}`
  );
  return res.data || { entries: [], total: 0 };
}

/**
 * Upload a file directly to S3 using a presigned URL.
 */
export async function uploadToPresignedUrl(
  url: string,
  file: File
): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });
  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }
}
